<template>
  <div>
    <!-- Replications List Card -->
    <b-card class="replications-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>

      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 replication-title">Tabulation Sheet Replications</h5>
          <small class="text-muted"
            >Manage and review tabulation sheet replications</small
          >
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Class Filter -->
        <b-col xl="2" lg="2" md="3" sm="6" cols="12">
          <v-select
            v-model="filters.classID"
            :options="classes"
            :clearable="true"
            label="name"
            :reduce="(val) => val.id"
            placeholder="Select class"
            class="filter-control v-select"
            append-to-body
            @input="onClassFilterChange"
          />
        </b-col>

        <!-- Subject Filter -->
        <b-col xl="2" lg="2" md="3" sm="6" cols="12">
          <v-select
            v-model="filters.subjectID"
            :options="subjects"
            :clearable="true"
            label="subject"
            :reduce="(val) => val.id"
            placeholder="Select subject"
            :disabled="!filters.classID"
            class="filter-control"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Exam Type Filter -->
        <b-col xl="2" lg="2" md="3" sm="6" cols="12">
          <v-select
            v-model="filters.examTypeID"
            :options="examTypes"
            :clearable="true"
            label="examType"
            :reduce="(val) => val.id"
            placeholder="Select exam type"
            class="filter-control v-select"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Session Filter -->
        <b-col xl="2" lg="2" md="3" sm="6" cols="12">
          <v-select
            v-model="filters.sessionID"
            :options="sessions"
            :clearable="true"
            label="session"
            :reduce="(val) => val.id"
            placeholder="Select session"
            class="filter-control"
            append-to-body
            @input="onFilterChange"
          />
        </b-col>

        <!-- Action Buttons -->
        <b-col
          xl="4"
          lg="4"
          md="12"
          sm="12"
          cols="12"
          class="text-right action-buttons-col"
        >
          <div
            class="d-flex gap-2 align-items-center justify-content-end action-buttons-container"
          >
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddModal"
              class="action-btn-filter add-replication-btn"
            >
              <feather-icon icon="PlusIcon" size="16" class="mr-1" />
              Create Replication
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-wrapper">
        <b-table
          class="replications-table"
          :items="replications"
          :fields="fields"
          :busy="dataLoading"
          :responsive="false"
          hover
          show-empty
          :empty-text="tableEmptyText"
        >
          <template #cell(sno)="{ index }">
            <span class="text-muted">{{
              (currentPage - 1) * perPage + index + 1
            }}</span>
          </template>
          <template #cell(sourceSheetName)="{ item }">
            <span class="table-cell-text font-weight-bold text-primary">
              {{ getSourceSheetDisplayName(item) }}
            </span>
          </template>
          <template #cell(className)="{ item }">
            <span class="table-cell-text">{{ item.className || "N/A" }}</span>
          </template>
          <template #cell(subjectNames)="{ item }">
            <span class="table-cell-text">{{
              item.metaSubjectNames || item.subjectNames || "N/A"
            }}</span>
          </template>
          <template #cell(examTypeName)="{ item }">
            <span class="table-cell-text">{{
              item.examTypeName || "N/A"
            }}</span>
          </template>
          <template #cell(sessionName)="{ item }">
            <span class="table-cell-text">{{ item.sessionName || "N/A" }}</span>
          </template>
          <template #cell(campusNames)="{ item }">
            <span class="table-cell-text">{{
              item.metaCampusNames || item.campusNames || "N/A"
            }}</span>
          </template>
          <template #cell(isActive)="{ item }">
            <div class="d-flex align-items-center justify-content-center">
              <b-spinner
                v-if="statusLoading === item.id"
                small
                type="grow"
                class="mr-2"
              />
              <b-form-checkbox
                :checked="item.isActive"
                switch
                @change="toggleStatus(item)"
                :disabled="statusLoading === item.id"
                class="status-toggle"
              />
            </div>
          </template>
          <template #cell(actions)="{ item }">
            <div
              class="d-flex align-items-center justify-content-end action-buttons-wrapper"
            >
              <b-button
                v-if="rights.edit"
                variant="outline-success"
                v-b-tooltip.hover.left
                title="Edit"
                size="sm"
                class="action-btn-outline edit-btn mr-1"
                :disabled="editLoading === item.id || deleteLoading === item.id"
                @click="editReplication(item)"
              >
                <b-spinner v-if="editLoading === item.id" small type="grow" />
                <feather-icon v-else icon="EditIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.right
                title="Delete"
                size="sm"
                class="action-btn-outline delete-btn"
                :disabled="editLoading === item.id || deleteLoading === item.id"
                @click="deleteReplication(item)"
              >
                <b-spinner v-if="deleteLoading === item.id" small type="grow" />
                <feather-icon v-else icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Replication' : 'Create Replication'"
      @hidden="resetForm"
      centered
      size="xl"
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <!-- Source Tabulation Sheet -->
        <b-row>
          <b-col md="12">
            <b-form-group
              label="Source Tabulation Sheet"
              label-for="sourceSheetId"
              required
            >
              <!-- Search Input -->
              <div class="search-section">
                <b-form-input
                  v-model="sourceSheetSearchQuery"
                  placeholder="Search tabulation sheets..."
                  @input="onSourceSheetSearchChange"
                  class="search-input input-compact"
                />
              </div>

              <!-- Search Results -->
              <div
                v-if="sourceSheetSearchQuery.length >= 2"
                class="search-results"
              >
                <div
                  v-if="filteredSourceSheetOptions.length > 0"
                  class="source-sheet-options"
                >
                  <div
                    v-for="sheet in filteredSourceSheetOptions"
                    :key="sheet.id"
                    class="source-sheet-option"
                    @click="selectSourceSheet(sheet)"
                  >
                    <div class="source-sheet-main">
                      <strong>{{ sheet.className || "N/A" }}</strong> -
                      {{ sheet.subjectName || "N/A" }}
                    </div>
                    <div class="source-sheet-details">
                      <span class="badge badge-light-primary mr-1">{{
                        sheet.examTypeName || "N/A"
                      }}</span>
                      <span class="badge badge-light-info mr-1">{{
                        sheet.sessionName || "N/A"
                      }}</span>
                      <span class="badge badge-light-success">{{
                        sheet.typeDisplayName || sheet.type || "N/A"
                      }}</span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    filteredSourceSheetOptions.length === 0 &&
                    !sourceSheetsLoading
                  "
                  class="empty-state"
                >
                  <feather-icon icon="SearchIcon" size="32" />
                  <p>No tabulation sheets found</p>
                </div>

                <div v-if="sourceSheetsLoading" class="loading-state">
                  <b-spinner variant="primary" size="sm" />
                  <span>Searching...</span>
                </div>
              </div>

              <!-- Selected Source Sheet Display -->
              <div
                v-if="form.sourceSheetId && selectedSourceSheet"
                class="selected-source-sheet"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{
                      selectedSourceSheet.className || "N/A"
                    }}</strong>
                    - {{ selectedSourceSheet.subjectName || "N/A" }}
                    <div class="text-muted small">
                      {{ selectedSourceSheet.examTypeName || "N/A" }} |
                      {{ selectedSourceSheet.sessionName || "N/A" }} |
                      {{
                        selectedSourceSheet.typeDisplayName ||
                        selectedSourceSheet.type ||
                        "N/A"
                      }}
                    </div>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="clearSelectedSourceSheet"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Campuses" label-for="campusIDs">
              <v-select
                v-model="form.campusIDs"
                :options="campuses"
                :clearable="false"
                label="branch"
                :reduce="(val) => val.id"
                :multiple="true"
                placeholder="Select campuses"
                @input="onCampusChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Class" label-for="classId">
              <v-select
                v-model="form.classId"
                :options="classOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class"
                :loading="modalClassesLoading"
                :disabled="
                  modalClassesLoading ||
                  !form.campusIDs ||
                  form.campusIDs.length === 0
                "
                @input="onClassChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Subject" label-for="subjectId">
              <v-select
                v-model="form.subjectId"
                :options="subjectOptions"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                placeholder="Select subject"
                :loading="modalSubjectsLoading"
                :disabled="
                  modalSubjectsLoading ||
                  !form.campusIDs ||
                  form.campusIDs.length === 0
                "
                @input="onSubjectChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Exam Type" label-for="examTypeId">
              <v-select
                v-model="form.examTypeId"
                :options="examTypes"
                :clearable="false"
                label="examType"
                :reduce="(val) => val.id"
                placeholder="Select exam type"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Session" label-for="sessionId">
              <v-select
                v-model="form.sessionId"
                :options="sessions"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
                placeholder="Select session"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="saveReplication"
          :disabled="loading || !isFormValid"
          class="btn-primary-custom"
        >
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Replicate" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BFormCheckbox,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";

export default {
  name: "TabulationSheetReplication",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BFormCheckbox,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      replications: [],
      campuses: [],
      classes: [],
      classesData: [],
      sections: [],
      subjects: [],
      examTypes: [],
      sessions: [],
      sourceSheets: [],
      sourceSheetsLoading: false,
      sourceSheetsPage: 1,
      sourceSheetsPerPage: 10, // Show only 10 items initially
      sourceSheetsTotal: 0,
      sourceSheetsSearch: "",
      sourceSheetSearchQuery: "",
      filteredSourceSheetOptions: [],
      selectedSourceSheet: null,
      sourceSheetSearchTimer: null,
      sourceSheetSearchCache: {},
      modalClasses: [],
      modalSections: [],
      modalSubjects: [],
      modalClassesLoading: false,
      modalSubjectsLoading: false,
      searchQuery: "",
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      editLoading: null,
      deleteLoading: null,
      statusLoading: null,
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      rights: {},
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { key: "sno", label: "S.No", sortable: false },
        { key: "sourceSheetName", label: "Source Sheet" },
        { key: "className", label: "Class" },
        { key: "subjectNames", label: "Subjects" },
        { key: "examTypeName", label: "Exam Type" },
        { key: "sessionName", label: "Session" },
        { key: "campusNames", label: "Campuses" },
        { key: "isActive", label: "Status" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      form: {
        id: 0,
        sourceSheetId: null,
        campusIDs: [],
        classId: null,
        sectionId: 0, // Default to 0 (All sections)
        subjectId: null,
        examTypeId: null,
        sessionId: null,
        isActive: true,
      },
      filters: {
        classID: null,
        subjectID: null,
        examTypeID: null,
        sessionID: null,
      },
    };
  },
  computed: {
    displayedRange() {
      const start =
        this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    tableEmptyText() {
      if (this.dataLoading) return "Loading...";
      return "No replications found";
    },
    classOptions() {
      return this.modalClasses || [];
    },
    sectionOptions() {
      return this.modalSections || [];
    },
    subjectOptions() {
      return this.modalSubjects || [];
    },
    reportDomain() {
      return String(
        this.$store.state.report || this.$store.state.clientID || ""
      )
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
    },
    isHpgsDomain() {
      return (
        this.reportDomain === "myskoolhpgs" ||
        this.reportDomain === "hpgs" ||
        this.reportDomain.includes("hpgs")
      );
    },
    isFormValid() {
      return (
        this.form.sourceSheetId !== null &&
        this.form.sourceSheetId !== undefined &&
        Array.isArray(this.form.campusIDs) &&
        this.form.campusIDs.length > 0 &&
        this.form.classId !== null &&
        this.form.subjectId !== null &&
        this.form.examTypeId !== null &&
        this.form.sessionId !== null
      );
    },
  },
  async created() {
    // Initialize rights and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await this.loadReplications();
    await this.loadCampuses();
    await this.loadClasses();
    await this.loadExamTypes();
    await this.loadSessions();
  },
  watch: {
    "filters.classID"(newVal) {
      if (newVal) {
        this.loadSubjects();
      } else {
        this.subjects = [];
        this.filters.subjectID = null;
      }
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    // HPGS keeps a single master class/subject catalog at Head Office (campus 1),
    // so the modal always loads that catalog instead of the selected campus's.
    getCatalogCampusId(campusId) {
      return this.isHpgsDomain ? 1 : campusId;
    },

    async loadCampuses() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },

    async loadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const classesData = response.data || response;
        this.classesData = Array.isArray(classesData) ? classesData : [];
        this.classes = this.classesData.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },

    async loadSubjects() {
      if (!this.filters.classID) {
        this.subjects = [];
        return;
      }
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&classID=" +
            this.filters.classID +
            "&sectionID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.subjects = response.data || response || [];
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },

    async loadExamTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&status=active&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.examTypes = (response.data || response || []).map((item) => ({
          id: item.id,
          examType: item.examType,
        }));
      } catch (error) {
        console.error("Error loading exam types:", error);
        this.examTypes = [];
      }
    },

    async loadSessions() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.sessions = (response.data || response || []).map((item) => ({
          id: item.id,
          session: item.session,
        }));
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    async loadExamTypesForCampus(campusId) {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusId +
            "&status=active&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.examTypes = (response.data || response || []).map((item) => ({
          id: item.id,
          examType: item.examType,
        }));
      } catch (error) {
        console.error("Error loading exam types for campus:", error);
        this.examTypes = [];
      }
    },

    async loadSessionsForCampus(campusId) {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.sessions = (response.data || response || []).map((item) => ({
          id: item.id,
          session: item.session,
        }));
      } catch (error) {
        console.error("Error loading sessions for campus:", error);
        this.sessions = [];
      }
    },

    async loadSourceSheets() {
      this.sourceSheetsLoading = true;
      try {
        // Load all data by using a very large pageSize
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: 1,
          pageSize: 10000, // Load all data at once
        });

        // Add search parameter if provided
        if (this.sourceSheetsSearch && this.sourceSheetsSearch.trim()) {
          params.append("search", this.sourceSheetsSearch.trim());
        }

        let url =
          this.$store.state.domain +
          "TabulationSheet/GetByPagination?" +
          params.toString();

        const obj = {
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);

        // When dashboard: true, get() returns the full response object
        // Response structure: { status: "success", data: [...], totalCount: 2 }
        let sheets = [];
        if (res && res.status === "success" && Array.isArray(res.data)) {
          sheets = res.data;
          this.sourceSheetsTotal = res.totalCount || res.total || sheets.length;
        } else if (res && Array.isArray(res.data)) {
          // Fallback for different response structure
          sheets = res.data;
          this.sourceSheetsTotal = res.total || res.totalCount || sheets.length;
        } else if (Array.isArray(res)) {
          // Fallback: if response is directly an array
          sheets = res;
          this.sourceSheetsTotal = sheets.length;
        } else {
          sheets = [];
          this.sourceSheetsTotal = 0;
        }

        // Map sheets to the format needed for search results
        if (sheets && sheets.length > 0) {
          const mappedSheets = sheets.map((sheet) => {
            // Map type to display name
            let typeDisplayName = sheet.type;
            if (sheet.type === "questions-base")
              typeDisplayName = "Questions Base";
            else if (sheet.type === "icon-base") typeDisplayName = "Icon Base";
            else if (sheet.type === "class-test")
              typeDisplayName = "Class Test";

            return {
              id: sheet.id,
              className: sheet.className || "N/A",
              subjectName: sheet.subjectName || "N/A",
              examTypeName: sheet.examTypeName || "N/A",
              sessionName: sheet.sessionName || "N/A",
              type: sheet.type,
              typeDisplayName: sheet.typeDisplayName || typeDisplayName,
              displayName: `${sheet.className || "N/A"} | ${
                sheet.subjectName || "N/A"
              } | ${sheet.examTypeName || "N/A"} | ${
                sheet.sessionName || "N/A"
              }`,
            };
          });
          this.sourceSheets = mappedSheets;
          this.filteredSourceSheetOptions = mappedSheets;
        } else {
          this.sourceSheets = [];
          this.filteredSourceSheetOptions = [];
        }
      } catch (error) {
        console.error("Error loading source sheets:", error);
        this.sourceSheets = [];
      } finally {
        this.sourceSheetsLoading = false;
      }
    },

    onSourceSheetSearchChange() {
      // Debounce to reduce API hits
      if (this.sourceSheetSearchTimer)
        clearTimeout(this.sourceSheetSearchTimer);
      this.sourceSheetSearchTimer = setTimeout(() => {
        this.performSourceSheetSearch();
      }, 700); // Debounce time similar to student search
    },

    async performSourceSheetSearch() {
      const term = (this.sourceSheetSearchQuery || "").trim();
      if (term.length < 2) {
        this.filteredSourceSheetOptions = [];
        this.sourceSheetsSearch = "";
        return;
      }

      // If we already searched this term, use cache to avoid extra API hit
      if (this.sourceSheetSearchCache[term.toLowerCase()]) {
        this.filteredSourceSheetOptions =
          this.sourceSheetSearchCache[term.toLowerCase()];
        this.sourceSheetsSearch = term;
        return;
      }

      this.sourceSheetsLoading = true;
      try {
        this.sourceSheetsSearch = term;
        await this.loadSourceSheets();

        // Cache results for this term
        this.sourceSheetSearchCache[term.toLowerCase()] =
          this.filteredSourceSheetOptions;
      } catch (error) {
        console.error("Source sheet search error:", error);
        this.filteredSourceSheetOptions = [];
      } finally {
        this.sourceSheetsLoading = false;
      }
    },

    selectSourceSheet(sheet) {
      this.form.sourceSheetId = sheet.id;
      this.selectedSourceSheet = sheet;
      this.sourceSheetSearchQuery = "";
      this.filteredSourceSheetOptions = [];
      this.onSourceSheetChange();
    },

    clearSelectedSourceSheet() {
      this.form.sourceSheetId = null;
      this.selectedSourceSheet = null;
      this.sourceSheetSearchQuery = "";
      this.filteredSourceSheetOptions = [];
    },

    async loadModalClasses() {
      if (!this.form.campusIDs || this.form.campusIDs.length === 0) {
        this.modalClasses = [];
        this.form.classId = null;
        return;
      }

      this.modalClassesLoading = true;
      try {
        // Load classes only from the FIRST selected campus (HPGS: Head Office catalog)
        const firstCampusId = this.getCatalogCampusId(this.form.campusIDs[0]);
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            firstCampusId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const classesData = response.data || response;
        this.modalClasses = Array.isArray(classesData)
          ? classesData.map((el) => el.cls)
          : [];
      } catch (error) {
        console.error("Error loading modal classes:", error);
        this.modalClasses = [];
        this.form.classId = null;
      } finally {
        this.modalClassesLoading = false;
      }
    },

    loadSectionsFromClass(classId) {
      if (!classId) {
        this.modalSections = [];
        return;
      }
      // Find the selected class in classesData
      const selectedClassData = this.classesData.find(
        (item) => item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.modalSections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section,
          name: sec.section,
        }));
      } else {
        this.modalSections = [];
      }
    },

    async loadModalSubjects() {
      if (!this.form.campusIDs || this.form.campusIDs.length === 0) {
        this.modalSubjects = [];
        this.form.subjectId = null;
        return;
      }

      this.modalSubjectsLoading = true;
      try {
        // Load subjects only from the FIRST selected campus (HPGS: Head Office catalog)
        const firstCampusId = this.getCatalogCampusId(this.form.campusIDs[0]);
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            firstCampusId,
          token: this.$store.state.userData.token,
        };
        this.modalSubjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading modal subjects:", error);
        this.modalSubjects = [];
        this.form.subjectId = null;
      } finally {
        this.modalSubjectsLoading = false;
      }
    },

    async loadReplications() {
      this.dataLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "TabulationSheetReplication/GetByPagination?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&page=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;

        if (this.filters.classID) url += "&classId=" + this.filters.classID;
        if (this.filters.subjectID)
          url += "&subjectId=" + this.filters.subjectID;
        if (this.filters.examTypeID)
          url += "&examTypeId=" + this.filters.examTypeID;
        if (this.filters.sessionID)
          url += "&sessionId=" + this.filters.sessionID;

        const obj = {
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.replications = res.data;
          this.totalCount = res.totalCount || res.total || 0;
        } else if (res && Array.isArray(res.data)) {
          // Fallback for different response structure
          this.replications = res.data;
          this.totalCount = res.total || res.totalCount || 0;
        } else {
          this.replications = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading replications:", error);
        this.replications = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    async onFilterChange() {
      this.currentPage = 1;
      this.loadReplications();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadReplications();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadReplications();
    },

    async onClassFilterChange() {
      if (this.filters.classID) {
        await this.loadSubjects();
      }
      this.onFilterChange();
    },

    async onCampusChange() {
      this.form.classId = null;
      this.form.sectionId = 0; // Set to 0 by default (All sections)
      this.form.subjectId = null;
      this.form.examTypeId = null;
      this.form.sessionId = null;
      this.modalClasses = [];
      this.modalSections = [];
      this.modalSubjects = [];

      if (this.form.campusIDs && this.form.campusIDs.length > 0) {
        const firstCampusId = this.form.campusIDs[0];
        await this.loadModalClasses();
        await this.loadModalSubjects();
        await this.loadExamTypesForCampus(firstCampusId);
        await this.loadSessionsForCampus(firstCampusId);
      }
    },

    async onClassChange() {
      // Set sectionId to 0 by default (All sections)
      this.form.sectionId = 0;

      // If multiple campuses selected, find matching class names in other campuses
      if (
        this.form.campusIDs &&
        this.form.campusIDs.length > 1 &&
        this.form.classId
      ) {
        await this.findMatchingClassIds();
      }
    },

    async findMatchingClassIds() {
      if (
        !this.form.classId ||
        !this.form.campusIDs ||
        this.form.campusIDs.length <= 1
      ) {
        return;
      }

      // HPGS classes come from the Head Office catalog (cID 1), so the
      // selected class ID already applies to every campus.
      if (this.isHpgsDomain) return;

      try {
        // Get the selected class name from first campus
        const selectedClass = this.modalClasses.find(
          (c) => c.id === this.form.classId
        );
        if (!selectedClass) return;

        const className = selectedClass.name || selectedClass.className || "";
        if (!className) return;

        // Load classes from other campuses (skip first campus as we already have it)
        const otherCampusIds = this.form.campusIDs.slice(1);
        const classPromises = otherCampusIds.map(async (campusId) => {
          const obj = {
            url:
              this.$store.state.domain +
              "myclasses/LoadClasses?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              campusId +
              "&dID=0",
            token: this.$store.state.userData.token,
          };
          const response = await this.get(obj);
          const classesData = response.data || response;
          return Array.isArray(classesData)
            ? classesData.map((el) => el.cls)
            : [];
        });

        const otherCampusClasses = await Promise.all(classPromises);

        // Find matching class IDs by name
        const matchingClassIds = [this.form.classId]; // Start with selected class ID

        otherCampusClasses.forEach((campusClasses) => {
          const matchingClass = campusClasses.find(
            (cls) => (cls.name || cls.className || "") === className
          );
          if (matchingClass) {
            matchingClassIds.push(matchingClass.id);
          }
        });

        // Store matching class IDs in the selected class object for later use
        if (selectedClass) {
          selectedClass.allClassIds = matchingClassIds;
        }
      } catch (error) {
        console.error("Error finding matching class IDs:", error);
      }
    },

    async onSubjectChange(subjectId) {
      if (!subjectId) return;

      // If multiple campuses selected, find matching subject names in other campuses
      if (this.form.campusIDs && this.form.campusIDs.length > 1) {
        await this.findMatchingSubjectIds(subjectId);
      }
    },

    async findMatchingSubjectIds(selectedSubjectId) {
      if (
        !selectedSubjectId ||
        !this.form.campusIDs ||
        this.form.campusIDs.length <= 1
      ) {
        return;
      }

      // HPGS subjects come from the Head Office catalog (cID 1), so the
      // selected subject ID already applies to every campus.
      if (this.isHpgsDomain) return;

      try {
        // Get selected subject name from first campus
        const selectedSubject = this.modalSubjects.find(
          (s) => s.id === selectedSubjectId
        );
        if (!selectedSubject) return;

        const subjectName =
          selectedSubject.subject || selectedSubject.name || "";
        if (!subjectName) return;

        // Load subjects from other campuses (skip first campus as we already have it)
        const otherCampusIds = this.form.campusIDs.slice(1);
        const subjectPromises = otherCampusIds.map(async (campusId) => {
          const obj = {
            url:
              this.$store.state.domain +
              "Subjects?db=" +
              this.$store.state.userData.db +
              "&cId=" +
              campusId,
            token: this.$store.state.userData.token,
          };
          return await this.get(obj);
        });

        const otherCampusSubjects = await Promise.all(subjectPromises);

        // Find matching subject IDs by name
        const matchingSubjectIds = [selectedSubjectId]; // Start with selected subject ID

        otherCampusSubjects.forEach((campusSubjects) => {
          const matchingSubject = (campusSubjects || []).find(
            (subj) => (subj.subject || subj.name || "") === subjectName
          );
          if (matchingSubject) {
            matchingSubjectIds.push(matchingSubject.id);
          }
        });

        // Store matching subject IDs in the selected subject object for later use
        if (selectedSubject) {
          selectedSubject.allSubjectIds = matchingSubjectIds;
        }
      } catch (error) {
        console.error("Error finding matching subject IDs:", error);
      }
    },

    onSourceSheetChange() {
      // Optionally load source sheet details to pre-fill some fields
    },

    async openAddModal() {
      this.isEdit = false;
      this.modalClasses = [];
      this.modalSections = [];
      this.modalSubjects = [];
      this.modalClassesLoading = false;
      this.modalSubjectsLoading = false;

      // Reset source sheet search
      this.sourceSheetSearchQuery = "";
      this.filteredSourceSheetOptions = [];
      this.selectedSourceSheet = null;

      this.showModal = true;
    },

    async editReplication(item) {
      this.editLoading = item.id;
      this.isEdit = true;
      this.loading = true;

      try {
        // Load full replication data using LoadSelected API
        const obj = {
          url:
            this.$store.state.domain +
            "TabulationSheetReplication/LoadSelected?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response.data || response;

        // Set form with campus IDs first
        this.form = {
          id: data.id,
          sourceSheetId: data.sourceSheetId,
          campusIDs: data.campusIDs || [],
          classId: null, // Will be set after loading classes
          sectionId: data.sectionId || 0,
          subjectId: null, // Will be set after loading subjects
          examTypeId: null, // Will be set after loading exam types
          sessionId: null, // Will be set after loading sessions
          isActive: data.isActive,
        };

        // Load and set source sheet for edit mode
        if (data.sourceSheetId) {
          try {
            // Search for the source sheet to get its details
            this.sourceSheetsSearch = "";
            await this.loadSourceSheets();
            // Find the selected sheet from loaded sheets
            const foundSheet = this.sourceSheets.find(
              (sheet) => sheet.id === data.sourceSheetId
            );
            if (foundSheet) {
              this.selectedSourceSheet = foundSheet;
            } else {
              // If not found, try to construct from item data (the row data)
              // This is a fallback when sheet isn't found in search results
              const sourceSheetType = item.sourceSheetType || "N/A";
              let typeDisplayName = sourceSheetType;
              if (sourceSheetType === "questions-base")
                typeDisplayName = "Questions Base";
              else if (sourceSheetType === "icon-base")
                typeDisplayName = "Icon Base";
              else if (sourceSheetType === "class-test")
                typeDisplayName = "Class Test";

              this.selectedSourceSheet = {
                id: data.sourceSheetId,
                className: item.className || "N/A",
                subjectName:
                  item.metaSubjectNames || item.subjectNames || "N/A",
                examTypeName: item.examTypeName || "N/A",
                sessionName: item.sessionName || "N/A",
                type: sourceSheetType,
                typeDisplayName: typeDisplayName,
              };
            }
            this.sourceSheetSearchQuery = "";
            this.filteredSourceSheetOptions = [];
          } catch (error) {
            console.error("Error loading source sheet for edit:", error);
          }
        }

        // Load data based on selected campuses
        if (this.form.campusIDs && this.form.campusIDs.length > 0) {
          // Load classes and subjects based on campus
          await this.loadModalClasses();
          await this.loadModalSubjects();

          // Load exam types and sessions based on first campus
          const firstCampusId = this.form.campusIDs[0];
          await this.loadExamTypesForCampus(firstCampusId);
          await this.loadSessionsForCampus(firstCampusId);

          // Now set the form values after all data is loaded
          this.form.classId = data.classId ? parseInt(data.classId) : null;
          this.form.subjectId = data.subjectId
            ? parseInt(data.subjectId)
            : data.subjectIds && data.subjectIds.length > 0
            ? parseInt(data.subjectIds[0])
            : null;
          this.form.examTypeId = data.examTypeId
            ? parseInt(data.examTypeId)
            : null;
          this.form.sessionId = data.sessionId
            ? parseInt(data.sessionId)
            : null;
        } else {
          // If no campuses, load global data
          if (this.examTypes.length === 0) {
            await this.loadExamTypes();
          }
          if (this.sessions.length === 0) {
            await this.loadSessions();
          }
          this.form.examTypeId = data.examTypeId
            ? parseInt(data.examTypeId)
            : null;
          this.form.sessionId = data.sessionId
            ? parseInt(data.sessionId)
            : null;
        }

        // Force update to ensure v-select displays correct values
        await this.$nextTick();
        this.$forceUpdate();
      } catch (error) {
        console.error("Error loading replication:", error);
        this.$bvToast.toast("Failed to load replication data", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
        this.editLoading = null;
        this.showModal = true;
      }
    },

    async deleteReplication(item) {
      const result = await this.$swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        this.deleteLoading = item.id;
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "TabulationSheetReplication/Delete?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&id=" +
              item.id,
            message: "Replication deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.delete(obj);
          if (success) {
            this.loadReplications();
          }
        } catch (error) {
          console.error("Error deleting replication:", error);
        } finally {
          this.deleteLoading = null;
        }
      }
    },

    async saveReplication() {
      if (!this.form.sourceSheetId) {
        this.$bvToast.toast("Please select a source tabulation sheet!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.form.campusIDs || this.form.campusIDs.length === 0) {
        this.$bvToast.toast("Please select at least one campus!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.form.classId) {
        this.$bvToast.toast("Please select a class!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Ensure sectionId is set to 0 (All sections) if not set
      if (this.form.sectionId === null || this.form.sectionId === undefined) {
        this.form.sectionId = 0;
      }

      if (!this.form.subjectId) {
        this.$bvToast.toast("Please select a subject!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.form.examTypeId) {
        this.$bvToast.toast("Please select an exam type!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.form.sessionId) {
        this.$bvToast.toast("Please select a session!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      // Extract names from selected options
      const selectedClass = this.modalClasses.find(
        (c) => c.id === this.form.classId
      );
      const className = selectedClass
        ? selectedClass.name || selectedClass.className || ""
        : "";

      const selectedExamType = this.examTypes.find(
        (et) => et.id === this.form.examTypeId
      );
      const examTypeName = selectedExamType
        ? selectedExamType.examType || ""
        : "";

      const selectedSession = this.sessions.find(
        (s) => s.id === this.form.sessionId
      );
      const sessionName = selectedSession ? selectedSession.session || "" : "";

      const selectedSubject = this.modalSubjects.find(
        (s) => s.id === this.form.subjectId
      );

      // For multiple campuses, collect all matching subject IDs
      let subjectIdsToSend = [this.form.subjectId];
      if (
        this.form.campusIDs.length > 1 &&
        selectedSubject &&
        selectedSubject.allSubjectIds &&
        selectedSubject.allSubjectIds.length > 1
      ) {
        subjectIdsToSend = selectedSubject.allSubjectIds;
      }

      const payload = {
        sourceSheetId: this.form.sourceSheetId,
        campusIDs: this.form.campusIDs,
        className: className,
        examTypeName: examTypeName,
        sessionName: sessionName,
        sectionId: 0,
      };

      if (this.isHpgsDomain) {
        // HPGS resolves the replicated subject by name (one subject per replication)
        payload.subjectNames = [
          (selectedSubject &&
            (selectedSubject.subject || selectedSubject.name)) ||
            "",
        ];
      } else {
        payload.subjectIds = subjectIdsToSend;
      }

      // Add id for edit mode
      if (this.isEdit) {
        payload.id = this.form.id;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "TabulationSheetReplication/Replicate?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&userID=" +
          this.$store.state.userData.userID,
        body: payload,
        // Return the full API body so per-campus errors (e.g. a class that is
        // not active in some campuses) can be shown together with skippedCampusIds.
        isBulkUpload: true,
        context: this,
        token: this.$store.state.userData.token,
      };

      const res = await this.post(obj);
      this.loading = false;

      if (res && res.status === "success") {
        this.$bvToast.toast(
          "Replication " +
            (this.isEdit ? "updated" : "created") +
            " successfully.",
          {
            title: "Notification",
            variant: "success",
            solid: true,
          }
        );
        this.showModal = false;
        this.loadReplications();
        return;
      }

      if (res && res.status === "error") {
        const skippedCampusNames = (res.skippedCampusIds || [])
          .map((id) => {
            const campus = this.campuses.find(
              (c) => String(c.id) === String(id)
            );
            return campus ? campus.branch : "Campus " + id;
          })
          .filter(Boolean);

        let errorText = res.message || "Replication failed.";
        if (skippedCampusNames.length > 0) {
          errorText += " Skipped campus(es): " + skippedCampusNames.join(", ");
        }

        this.$bvToast.toast(errorText, {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async toggleStatus(item) {
      this.statusLoading = item.id;
      const newStatus = !item.isActive;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "TabulationSheetReplication/UpdateStatus?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id +
            "&status=" +
            newStatus,
          message: "Status updated successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.post(obj);
        if (success) {
          item.isActive = newStatus;
          this.loadReplications();
        } else {
          // Revert on failure
          this.$forceUpdate();
        }
      } catch (error) {
        console.error("Error toggling status:", error);
        this.$bvToast.toast("Failed to update status", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        // Revert the toggle on error
        this.$forceUpdate();
      } finally {
        this.statusLoading = null;
      }
    },

    resetForm() {
      this.form = {
        id: 0,
        sourceSheetId: null,
        campusIDs: [],
        classId: null,
        sectionId: 0, // Default to 0 (All sections)
        subjectId: null,
        examTypeId: null,
        sessionId: null,
        isActive: true,
      };
      this.modalSections = [];
      this.modalClasses = [];
      this.modalSubjects = [];
      this.modalClassesLoading = false;
      this.modalSubjectsLoading = false;
      // Reset source sheet search
      this.sourceSheetSearchQuery = "";
      this.filteredSourceSheetOptions = [];
      this.selectedSourceSheet = null;
    },

    getSourceSheetDisplayName(item) {
      // Construct display name from available data
      if (item.sourceSheetName) {
        return item.sourceSheetName;
      }

      // Build from available fields
      const parts = [];
      if (item.className) parts.push(item.className);
      if (item.metaSubjectNames || item.subjectNames) {
        parts.push(item.metaSubjectNames || item.subjectNames);
      }
      if (item.examTypeName) parts.push(item.examTypeName);
      if (item.sessionName) parts.push(item.sessionName);
      if (item.sourceSheetType) {
        const typeName =
          item.sourceSheetType === "questions-base"
            ? "Questions Base"
            : item.sourceSheetType === "icon-base"
            ? "Icon Base"
            : item.sourceSheetType === "class-test"
            ? "Class Test"
            : item.sourceSheetType;
        parts.push(`(${typeName})`);
      }

      return parts.length > 0
        ? parts.join(" | ")
        : `Sheet ID: ${item.sourceSheetId || "N/A"}`;
    },
  },
};
</script>

<style lang="scss" scoped>
// Main Card Styling
.replications-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.gradient-line-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  border-radius: 20px 20px 0 0;
  z-index: 1;
}

// Table Header
.table-header {
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Filter Row
.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }
}

// Filter Control
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
}

// Action Buttons
.action-buttons-col {
  padding-right: 1.5rem !important;
}

.action-buttons-container {
  padding-right: 0.5rem;
}

.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2);
  }
}

.add-replication-btn {
  margin-right: 0 !important;
}

// Table Styling
.table-scroll-wrapper {
  overflow-x: auto;
  margin: 0 -0.5rem;
  padding: 0 0.5rem;
}

.replications-table {
  margin-top: 0;
  border-radius: 12px;
  overflow: visible;
  border: 1px solid #e5e7eb;
  width: 100%;
  table-layout: auto;

  ::v-deep thead th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 1rem 0.75rem;
    border-bottom: 2px solid #e0d8ff;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;

      td {
        background: #f1f5f9 !important;
      }
    }
  }

  ::v-deep tbody td {
    padding: 1rem 0.75rem;
    border-bottom: 1px solid #f0edff;
    vertical-align: middle;
    font-size: 0.875rem;
    color: #475569;
  }
}

// Action Buttons in Table
.action-buttons-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-right: 0;
}

::v-deep .replications-table {
  thead th:last-child {
    text-align: right !important;
    padding-right: 1rem;
  }
  tbody td:last-child {
    text-align: right !important;
    padding-right: 1rem;
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  &.edit-btn {
    border-color: #10b981 !important;
    color: #10b981 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);

    &:hover:not(:disabled) {
      background: #f0fdf4 !important;
      border-color: #059669 !important;
      color: #059669 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    }
  }

  &.delete-btn {
    border-color: #ef4444 !important;
    color: #ef4444 !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

    &:hover:not(:disabled) {
      background: #fef2f2 !important;
      border-color: #dc2626 !important;
      color: #dc2626 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Pagination Controls Top
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }
}

.page-size-select-compact {
  min-width: 80px;
  width: 80px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px;
    padding: 0 0.5rem;
    font-size: 0.8125rem;
  }
}

.pagination-count-display {
  flex: 1;
  text-align: center;
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 500;

  .count-text {
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.pagination-compact {
  margin: 0;

  ::v-deep .page-item {
    margin: 0 2px;

    .page-link {
      border-radius: 6px;
      border: 1px solid #e0d8ff;
      color: #7c5dfa;
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        transform: translateY(-2px);
      }
    }

    &.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: white;
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
    }

    &.disabled .page-link {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.table-cell-text {
  color: #475569;
  font-weight: 500;
  font-size: 0.875rem;
}

// Status Toggle Styling
.status-toggle {
  flex-shrink: 0;

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #7c5dfa;
    border-color: #7c5dfa;
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 0.2rem rgba(124, 93, 250, 0.25);
  }

  ::v-deep .custom-switch .custom-control-label::before {
    width: 2.5rem;
    height: 1.25rem;
    border-radius: 0.625rem;
    background-color: #cbd5e1;
    border-color: #cbd5e1;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
  }

  ::v-deep .custom-switch .custom-control-label::after {
    width: calc(1.25rem - 4px);
    height: calc(1.25rem - 4px);
    border-radius: 50%;
    background-color: #ffffff;
    transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::after {
    transform: translateX(1.25rem);
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Modal Styles
::v-deep .modal-body {
  overflow: visible !important;
}

::v-deep .modal-content {
  overflow: visible !important;
}

// Source sheet search functionality styles
.search-section {
  margin-bottom: 16px;
}

.search-input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.input-compact {
  height: 40px !important;
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.45rem 0.75rem !important;
  font-size: 0.95rem !important;
}

.input-compact:focus {
  border-color: #7c5dfa !important;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.12) !important;
}

.search-results {
  position: relative;
  z-index: 1000;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  overflow-x: hidden;
}

.source-sheet-options {
  max-height: none;
  overflow: visible;
}

.source-sheet-option {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
}

.source-sheet-option:hover {
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.source-sheet-option:last-child {
  border-bottom: none;
}

.source-sheet-option:active {
  transform: translateX(1px);
  background-color: #e3f2fd;
}

.source-sheet-main {
  font-size: 14px;
  margin-bottom: 4px;
  color: #495057;
}

.source-sheet-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.loading-state {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.selected-source-sheet {
  margin-top: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

// Custom scrollbar for search results
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.2s ease;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

:deep(.vs__dropdown-menu) {
  z-index: 1055 !important;
  position: fixed !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

:deep(.vs--open .vs__dropdown-menu) {
  z-index: 1055 !important;
  position: fixed !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

:deep(.vs__dropdown-toggle) {
  position: relative;
  z-index: 1;
}

:deep(.vs--open .vs__dropdown-toggle) {
  z-index: 1055;
}

// Modal specific dropdown fixes
::v-deep .modal {
  .vs__dropdown-menu {
    z-index: 1055 !important;
    position: fixed !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .vs--open .vs__dropdown-menu {
    z-index: 1055 !important;
    position: fixed !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .vs__dropdown-toggle {
    position: relative;
    z-index: 1;
  }

  .vs--open .vs__dropdown-toggle {
    z-index: 1055;
  }
}
</style>
